import { useEffect, useMemo, useRef, useState } from 'react';
import { userApi } from '../../../core/api/rtk-query/user.api';
import styled from 'styled-components';
import { UserEditModal } from './UserEditModal';
import { groupApi } from '../../../core/api/rtk-query/group.api';

const Wrap = styled.div`display:grid; gap:12px;`;
const Toolbar = styled.div`display:flex; justify-content:flex-end; gap:8px; /* align-items:center; временно отключено */`;
const LightInput = styled.input`padding:10px 12px; border-radius:10px; border:1px solid #dbe3f6; background:#fff; color:#0b1221; width:100%; box-sizing:border-box;`;
const LightSelect = styled.select`padding:10px 12px; border-radius:10px; border:1px solid #dbe3f6; background:#fff; color:#0b1221; width:100%; box-sizing:border-box;`;
const TableWrap = styled.div`border:1px solid #e5e8f0; border-radius:14px; overflow:visible; background:#fff;`;
const Table = styled.table`
  width:100%; border-collapse:separate; border-spacing:0; color:#0b1221;
  thead th{position:sticky; top:0; background:#f7f9ff; color:#465676; padding:12px; text-align:left; border-bottom:1px solid #e5e8f0; min-width: 90px}
  thead th.active{ background:#eaf2ff !important; color:#0b68fe !important; }
  tbody td{border-bottom:1px solid #eef1f6; padding:12px}
`;
const Chip = styled.span`display:inline-flex; align-items:center; gap:6px; padding:4px 8px; border-radius:999px; font-size:12px; border:1px solid #dbe3f6; background:#f7f9ff; color:#0b1221;`;
const Status = styled.span<{ok:boolean}>`display:inline-block; padding:4px 8px; border-radius:999px; font-size:12px; border:1px solid ${p=>p.ok?'#c9e9dd':'#f5c2c7'}; background:${p=>p.ok?'#e6faf3':'#fde8ea'}; color:${p=>p.ok?'#03543f':'#9b1c1c'};`;
const Actions = styled.div`display:flex; gap:8px;`;
const Btn = styled.button`background:#fff; border:1px solid #dbe3f6; color:#0b1221; padding:8px 12px; border-radius:10px; cursor:pointer;`;
const PageBtn = styled.button`
  background:#fff; border:1px solid #dbe3f6; color:#0b1221; padding:8px 10px; border-radius:10px; min-width:36px; cursor:pointer;
  &[disabled]{opacity:.6; cursor:default}
`;
const Pagination = styled.div`display:flex; gap:6px; justify-content:flex-end; padding-top:10px;`;

// Комбобокс для фильтра групп (единый стиль, без обрезаний)
const ComboWrap = styled.div`
  position: relative;
`;
const ComboList = styled.ul`
  position: absolute; left: 0; right: 0; top: calc(100% + 6px);
  z-index: 50; list-style: none; margin: 0; padding: 8px 0; max-height: 240px; overflow: auto;
  background: #fff; border: 1px solid #dbe3f6; border-radius: 12px; box-shadow: 0 8px 28px rgba(0,0,0,.08);
`;
const ComboItem = styled.li`
  padding: 8px 12px; cursor: pointer; white-space: nowrap; text-overflow: ellipsis; overflow: hidden;
  &:hover{background:#f7f9ff}
`;

// Современный виджет пользователей: фильтры в шапке, live‑поиск, сортировка, экспорт отфильтрованных данных
export const AdminUsersPage = () => {
  const [users, setUsers] = useState<any[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [editUser, setEditUser] = useState<any|null>(null);
  const [groupOpen, setGroupOpen] = useState(false);
  const [selection, setSelection] = useState<Set<string>>(new Set());
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize] = useState(25);
  const [sortKey, setSortKey] = useState<'isuNumber'|'name'|'email'|'role'|'isuGroup'|'verified'>('name');
  const [sortDir, setSortDir] = useState<'asc'|'desc'>('asc');

  // фильтры
  const [fIsu, setFIsu] = useState('');
  const [fName, setFName] = useState('');
  const [fEmail, setFEmail] = useState('');
  const [fRole, setFRole] = useState('');
  const [fGroup, setFGroup] = useState('');
  const [fVerified, setFVerified] = useState('');

  const [updateUser] = userApi.useUpdateUserMutation();
  const [verifyUser] = userApi.useVerifyUserMutation();
  const [searchUsers] = userApi.useLazySearchUsersQuery();
  // Для админки используем админские эндпоинты, иначе /users/groups/active даёт 403 (Admin не в роли Student/Mentor/Teacher)
  const { data: adminGroups } = groupApi.useGetActiveAdminGroupsQuery({ pageNumber: 1, pageSize: 1000 });

  // дебаунс
  const debounceRef = useRef<number | undefined>(undefined);
  const debounce = (cb: () => void, delay = 300) => {
    window.clearTimeout(debounceRef.current);
    // @ts-ignore
    debounceRef.current = window.setTimeout(cb, delay);
  };

  // Единый запрос к бэкенду (RTK Query) + сортировка на сервере
  const args = useMemo(() => ({
    name: fName || undefined,
    email: fEmail || undefined,
    role: fRole || undefined,
    isuGroup: (fGroup && fGroup !== '__none__') ? fGroup : undefined,
    isuNumber: fIsu ? Number(fIsu) : undefined,
    verified: fVerified === '' ? undefined : fVerified === 'true',
    sortBy: sortKey,
    sortDir: sortDir,
    pageNumber,
    pageSize,
  }), [fName, fEmail, fRole, fGroup, fIsu, fVerified, pageNumber, pageSize, sortKey, sortDir]);

  const { data, isFetching, refetch } = userApi.useSearchUsersQuery(args);
  useEffect(() => { setLoadingUsers(isFetching); }, [isFetching]);
  useEffect(() => {
    let list = Array.isArray(data?.users) ? [...(data!.users as any[])] : [];
    list = list.sort((a, b) => {
      const va = a[sortKey]; const vb = b[sortKey];
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    if (fGroup === '__none__') list = list.filter(u => !u.isuGroup || !String(u.isuGroup).trim());
    setUsers(list);
  }, [data, sortKey, sortDir, fGroup]);
  const total = data?.number || 0;

  const onExportFiltered = async () => {
    // выгружаем отфильтрованных пользователей (поднимаем pageSize)
    setLoadingUsers(true);
    try {
      const res = await searchUsers({
        name: fName || undefined,
        email: fEmail || undefined,
        role: fRole || undefined,
        isuGroup: (fGroup && fGroup !== '__none__') ? fGroup : undefined,
        isuNumber: fIsu ? Number(fIsu) : undefined,
        verified: fVerified === '' ? undefined : fVerified === 'true',
        pageNumber: 1,
        pageSize: 5000,
      }).unwrap();
       let list = (res.users as any[]) || [];
       if (fGroup === '__none__') {
         list = list.filter(u => !u.isuGroup || !String(u.isuGroup).trim());
       }
      const headers = ['isuNumber','name','email','role','isuGroup','verified'];
      const csv = [headers.join(';'), ...list.map(u => headers.map(h => String(u[h] ?? '')).join(';'))].join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = 'users-filtered.csv'; a.click(); URL.revokeObjectURL(url);
    } finally { setLoadingUsers(false); }
  };

  const toggleSelect = (id: string, on: boolean) => {
    setSelection(prev => { const next = new Set(prev); if(on) next.add(id); else next.delete(id); return next; });
  };

  const massVerify = async (value: boolean) => {
    if (value) {
      await Promise.all(Array.from(selection).map(id => verifyUser({ id }).unwrap()));
      // мгновенно обновим UI
      setUsers(prev => prev.map(u => selection.has(u.id) ? { ...u, verified: true } : u));
    } else {
      // снятие верификации не поддержано отдельным эндпоинтом — пропускаем
    }
    setSelection(new Set()); refetch();
  };

  const massSetRole = async (role: string) => {
    await Promise.all(Array.from(selection).map(id => updateUser({ id, role }).unwrap()));
    // локально обновим
    setUsers(prev => prev.map(u => selection.has(u.id) ? { ...u, role } : u));
    setSelection(new Set()); refetch();
  };

  const massAddToGroup = async (groupName: string) => {
    await Promise.all(Array.from(selection).map(id => updateUser({ id, isuGroup: groupName }).unwrap()));
    setUsers(prev => prev.map(u => selection.has(u.id) ? { ...u, isuGroup: groupName } : u));
    setSelection(new Set()); refetch();
  };

  const groupSuggestions = useMemo(()=>{
    const byUsers = Array.from(new Set(users.map(u=>u.isuGroup).filter(Boolean)));
    const byApi = (adminGroups?.groups||[]).map(g=> g.name);
    return Array.from(new Set([...byApi, ...byUsers])).sort();
  }, [users, adminGroups]);

  return (
    <Wrap>
      <Toolbar>
        <Btn onClick={onExportFiltered} disabled={loadingUsers}>Экспорт CSV</Btn>
        {selection.size>0 && (
          <Actions>
            <Btn onClick={()=> massVerify(true)}>Верифицировать</Btn>
            <Btn onClick={()=> massVerify(false)}>Снять вериф.</Btn>
            <LightSelect onChange={e=> { if(!e.target.value) return; massSetRole(e.target.value); e.currentTarget.selectedIndex = 0; }} defaultValue="">
              <option value="" disabled>Сменить роль…</option>
              <option value="Student">Student</option>
              <option value="Mentor">Mentor</option>
              <option value="Teacher">Teacher</option>
              <option value="Admin">Admin</option>
            </LightSelect>
            <LightSelect onChange={e=> { if(!e.target.value) return; massAddToGroup(e.target.value); e.currentTarget.selectedIndex = 0; }} defaultValue="">
              <option value="" disabled>В группу…</option>
              {groupSuggestions.map(n=> (<option key={n} value={n}>{n}</option>))}
            </LightSelect>
          </Actions>
        )}
      </Toolbar>
      <TableWrap>
        <Table>
          <thead>
            <tr>
              <th></th>
              <th className={sortKey==='isuNumber'?'active':''} onClick={() => { setSortKey('isuNumber'); setSortDir(d=> d==='asc'?'desc':'asc' as any); }} style={{ cursor: 'pointer' }}>ИСУ</th>
              <th className={sortKey==='name'?'active':''} onClick={() => { setSortKey('name'); setSortDir(d=> d==='asc'?'desc':'asc' as any); }} style={{ cursor: 'pointer' }}>ФИО</th>
              <th className={sortKey==='email'?'active':''} onClick={() => { setSortKey('email'); setSortDir(d=> d==='asc'?'desc':'asc' as any); }} style={{ cursor: 'pointer' }}>Почта</th>
              <th className={sortKey==='role'?'active':''} onClick={() => { setSortKey('role'); setSortDir(d=> d==='asc'?'desc':'asc' as any); }} style={{ cursor: 'pointer' }}>Роль</th>
              <th className={sortKey==='isuGroup'?'active':''} onClick={() => { setSortKey('isuGroup'); setSortDir(d=> d==='asc'?'desc':'asc' as any); }} style={{ cursor: 'pointer' }}>Группа ИСУ</th>
              <th className={sortKey==='verified'?'active':''} onClick={() => { setSortKey('verified'); setSortDir(d=> d==='asc'?'desc':'asc' as any); }} style={{ cursor: 'pointer' }}>Статус</th>
              <th>Действия</th>
            </tr>
            <tr>
              <th></th>
              <th style={{position:'relative'}}>
                <LightInput placeholder="ИСУ" value={fIsu} onChange={e=> setFIsu(e.target.value)} />
                {fIsu && (<span onClick={()=> setFIsu('')} style={{position:'absolute', right:8, top:8, cursor:'pointer', color:'#98a1b3'}}>×</span>)}
              </th>
              <th style={{position:'relative'}}>
                <LightInput placeholder="ФИО" value={fName} onChange={e=> setFName(e.target.value)} />
                {fName && (<span onClick={()=> setFName('')} style={{position:'absolute', right:8, top:8, cursor:'pointer', color:'#98a1b3'}}>×</span>)}
              </th>
              <th style={{position:'relative'}}>
                <LightInput placeholder="Email" value={fEmail} onChange={e=> setFEmail(e.target.value)} />
                {fEmail && (<span onClick={()=> setFEmail('')} style={{position:'absolute', right:8, top:8, cursor:'pointer', color:'#98a1b3'}}>×</span>)}
              </th>
              <th>
                <LightSelect value={fRole} onChange={e=> setFRole(e.target.value)}>
                  <option value="">—</option>
                  <option value="Student">Student</option>
                  <option value="Mentor">Mentor</option>
                  <option value="Teacher">Teacher</option>
                  <option value="Admin">Admin</option>
                </LightSelect>
              </th>
              <th style={{position:'relative'}}>
                <ComboWrap onBlur={()=> setGroupOpen(false)}>
                  <LightInput placeholder="Группа ISU…" value={fGroup} onChange={e=> { setFGroup(e.target.value); setGroupOpen(true); }} onFocus={()=> setGroupOpen(true)} />
                  {groupOpen && (
                    <ComboList>
                      <ComboItem onMouseDown={()=> { setFGroup('__none__'); setGroupOpen(false); }}>__none__ <span style={{opacity:.6}}>без группы</span></ComboItem>
                      {groupSuggestions.filter(n=> n.toLowerCase().includes((fGroup||'').toLowerCase())).map(n=> (
                        <ComboItem key={n} onMouseDown={()=> { setFGroup(n); setGroupOpen(false); }}>{n}</ComboItem>
                      ))}
                    </ComboList>
                  )}
                </ComboWrap>
                {fGroup && (<span onClick={()=> setFGroup('')} style={{position:'absolute', right:8, top:8, cursor:'pointer', color:'#98a1b3'}}>×</span>)}
              </th>
              <th>
                <LightSelect value={fVerified} onChange={e=> setFVerified(e.target.value)}>
                  <option value="">—</option>
                  <option value="true">Верифицирован</option>
                  <option value="false">Не верифицирован</option>
                </LightSelect>
              </th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {users.map(u => (
              <tr key={u.id}>
                <td><input type="checkbox" checked={selection.has(u.id)} onChange={e=> toggleSelect(u.id, e.target.checked)} /></td>
                <td onClick={()=> setEditUser(u)} style={{cursor:'pointer'}}>{u.isuNumber}</td>
                <td>{u.name}</td>
                <td>{u.email}</td>
                <td><Chip>{u.role}</Chip></td>
                <td>
                  <button style={{background:'transparent', border:'none', color:'#0b68fe', cursor:'pointer', padding:0}}
                          onClick={()=> setFGroup(u.isuGroup || '')}>{u.isuGroup}</button>
                </td>
                <td><Status ok={!!u.verified}>{u.verified?'да':'нет'}</Status></td>
                <td>
                  <Actions>
                    <Btn onClick={()=> setEditUser(u)}>Редактировать</Btn>
                     <Btn onClick={async()=> { const id = u.id as string; await userApi.endpoints.deleteUser.initiate({ id }) as any; refetch(); }}>Удалить</Btn>
                  </Actions>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </TableWrap>
      <Pagination>
        {(() => {
          const pages = Math.max(1, Math.ceil(total / pageSize));
          const cur = Math.min(pageNumber, pages);
          const btn = (p:number, label?:string, disabled?:boolean) => (
            <PageBtn key={`p-${label||p}`} disabled={disabled||p===cur} onClick={()=> setPageNumber(p)}>{label||p}</PageBtn>
          );
          const elems: any[] = [];
          if (pages <= 9) {
            for(let i=1;i<=pages;i++) elems.push(btn(i));
          } else {
            elems.push(btn(1));
            if (cur>4) elems.push(btn(cur-2, '…', true));
            for(let i=Math.max(2, cur-2); i<=Math.min(pages-1, cur+2); i++) elems.push(btn(i));
            if (cur<pages-3) elems.push(btn(cur+2, '…', true));
            elems.push(btn(pages));
          }
          return elems;
        })()}
      </Pagination>

      {editUser && (
        <UserEditModal
          initial={editUser}
          groupOptions={groupSuggestions}
          onClose={() => setEditUser(null)}
          onSave={async payload => {
            // Обновляем основные данные пользователя
            await updateUser(payload).unwrap();
            
            // Если изменился статус верификации и пользователь должен быть верифицирован
            if (payload.verified && !editUser.verified) {
              await verifyUser({ id: payload.id }).unwrap();
            }
            
            setUsers(prev => prev.map(u => u.id===payload.id ? { ...u, ...payload } : u));
            await refetch();
          }}
        />
      )}
    </Wrap>
  );
};


